import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  apiFetch,
} from "../api/client";

import {
  tenantPath,
} from "../utils/tenantContext";

import "./PcapEvidencePanel.css";


async function requestJson(
  path,
  options = {}
) {
  const response =
    await apiFetch(
      tenantPath(path),
      options
    );

  let body = {};

  try {
    body =
      await response.json();
  } catch (_) {
    // Ignore empty body.
  }

  if (!response.ok) {
    throw new Error(
      body.detail ||
      `Request failed (${response.status})`
    );
  }

  return body;
}


function localDate(value) {
  if (!value) return "—";

  try {
    return new Date(
      value
    ).toLocaleString();
  } catch (_) {
    return String(value);
  }
}


function PcapEvidencePanel({
  captureId,
}) {
  const [record, setRecord] =
    useState(null);

  const [registered, setRegistered] =
    useState(false);

  const [custody, setCustody] =
    useState([]);

  const [caseReference, setCaseReference] =
    useState("");

  const [title, setTitle] =
    useState("");

  const [notes, setNotes] =
    useState("");

  const [retentionUntil, setRetentionUntil] =
    useState("");

  const [legalHold, setLegalHold] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");


  const applyRecord =
    useCallback(
      (value) => {
        setRecord(
          value || null
        );

        if (!value) {
          setCaseReference("");
          setTitle("");
          setNotes("");
          setRetentionUntil("");
          setLegalHold(false);
          return;
        }

        setCaseReference(
          value.case_reference || ""
        );

        setTitle(
          value.title || ""
        );

        setNotes(
          value.analyst_notes || ""
        );

        setLegalHold(
          Boolean(
            value.legal_hold
          )
        );

        setRetentionUntil(
          value.retention_until
            ? String(
                value.retention_until
              ).slice(0, 10)
            : ""
        );
      },
      []
    );


  const load =
    useCallback(
      async () => {
        if (!captureId) {
          setRegistered(false);
          applyRecord(null);
          setCustody([]);
          return;
        }

        try {
          setError("");

          const evidence =
            await requestJson(
              `/network-protection/pcap/evidence/${encodeURIComponent(
                captureId
              )}`
            );

          setRegistered(
            Boolean(
              evidence.registered
            )
          );

          applyRecord(
            evidence.evidence
          );

          if (evidence.registered) {
            const history =
              await requestJson(
                `/network-protection/pcap/evidence/${encodeURIComponent(
                  captureId
                )}/custody`
              );

            setCustody(
              Array.isArray(
                history.custody
              )
                ? history.custody
                : []
            );
          } else {
            setCustody([]);
          }
        } catch (loadError) {
          setError(
            loadError.message
          );
        }
      },
      [
        captureId,
        applyRecord,
      ]
    );


  useEffect(() => {
    load();
  }, [load]);


  const runAction =
    async (
      path,
      options = {}
    ) => {
      try {
        setBusy(true);
        setError("");

        await requestJson(
          path,
          options
        );

        await load();
      } catch (actionError) {
        setError(
          actionError.message
        );
      } finally {
        setBusy(false);
      }
    };


  const registerEvidence =
    () => {
      if (!captureId) return;

      const confirmed =
        window.confirm(
          "Register this PCAP as controlled evidence? Its current SHA-256 will become the original evidence hash."
        );

      if (!confirmed) return;

      runAction(
        `/network-protection/pcap/evidence/${encodeURIComponent(
          captureId
        )}/register`,
        {
          method: "POST",
        }
      );
    };


  const verifyIntegrity =
    () => {
      if (!captureId) return;

      runAction(
        `/network-protection/pcap/evidence/${encodeURIComponent(
          captureId
        )}/verify`,
        {
          method: "POST",
        }
      );
    };


  const saveMetadata =
    () => {
      if (!captureId) return;

      const retention =
        retentionUntil
          ? new Date(
              `${retentionUntil}T23:59:59Z`
            ).toISOString()
          : null;

      runAction(
        `/network-protection/pcap/evidence/${encodeURIComponent(
          captureId
        )}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            case_reference:
              caseReference || null,

            title:
              title || null,

            analyst_notes:
              notes || null,

            retention_until:
              retention,

            legal_hold:
              legalHold,
          }),
        }
      );
    };


  const markReviewed =
    () => {
      const confirmed =
        window.confirm(
          "Mark this evidence as reviewed?"
        );

      if (!confirmed) return;

      runAction(
        `/network-protection/pcap/evidence/${encodeURIComponent(
          captureId
        )}/review`,
        {
          method: "POST",
        }
      );
    };


  const archiveEvidence =
    () => {
      const confirmed =
        window.confirm(
          "Archive this evidence record? The PCAP will NOT be deleted or moved."
        );

      if (!confirmed) return;

      runAction(
        `/network-protection/pcap/evidence/${encodeURIComponent(
          captureId
        )}/archive`,
        {
          method: "POST",
        }
      );
    };


  if (!captureId) {
    return null;
  }


  return (
    <section className="pcapEvidencePanel">
      <div className="pcapEvidenceHeader">
        <div>
          <span>
            DIGITAL EVIDENCE
          </span>

          <h3>
            Evidence Management
          </h3>

          <p>
            Integrity verification,
            retention metadata,
            legal hold and
            append-only chain of custody.
          </p>
        </div>

        <div
          className={
            registered
              ? "pcapEvidenceState registered"
              : "pcapEvidenceState"
          }
        >
          {registered
            ? (
                record?.lifecycle_status ||
                "REGISTERED"
              ).toUpperCase()
            : "NOT REGISTERED"}
        </div>
      </div>

      {error ? (
        <div className="pcapEvidenceError">
          {error}
        </div>
      ) : null}

      {!registered ? (
        <div className="pcapEvidenceRegistration">
          <p>
            This capture has not yet been
            registered as controlled evidence.
          </p>

          <button
            type="button"
            disabled={busy}
            onClick={registerEvidence}
          >
            Register as Evidence
          </button>
        </div>
      ) : (
        <>
          <div className="pcapEvidenceMetrics">
            <div>
              <span>
                Evidence UUID
              </span>

              <strong>
                {record?.evidence_uuid || "—"}
              </strong>
            </div>

            <div>
              <span>
                Integrity
              </span>

              <strong>
                {
                  record?.integrity_status
                    ?.toUpperCase() ||
                  "UNVERIFIED"
                }
              </strong>
            </div>

            <div>
              <span>
                PCAP Bytes
              </span>

              <strong>
                {record?.pcap_bytes ?? "—"}
              </strong>
            </div>

            <div>
              <span>
                Registered
              </span>

              <strong>
                {localDate(
                  record?.first_registered_at
                )}
              </strong>
            </div>
          </div>

          <div className="pcapEvidenceHash">
            <span>
              Original SHA-256
            </span>

            <code>
              {
                record?.original_pcap_sha256 ||
                "—"
              }
            </code>

            <span>
              Current SHA-256
            </span>

            <code>
              {
                record?.current_pcap_sha256 ||
                "—"
              }
            </code>
          </div>

          <div className="pcapEvidenceActions">
            <button
              type="button"
              disabled={busy}
              onClick={verifyIntegrity}
            >
              Verify Integrity
            </button>

            <button
              type="button"
              disabled={
                busy ||
                record?.integrity_status !==
                  "verified" ||
                record?.lifecycle_status ===
                  "archived"
              }
              onClick={markReviewed}
            >
              Mark Reviewed
            </button>

            <button
              type="button"
              className="pcapArchiveButton"
              disabled={
                busy ||
                record?.integrity_status !==
                  "verified" ||
                record?.legal_hold ||
                record?.lifecycle_status ===
                  "archived"
              }
              onClick={archiveEvidence}
            >
              Archive Evidence
            </button>
          </div>

          <div className="pcapEvidenceForm">
            <label>
              Case Reference

              <input
                value={caseReference}
                disabled={busy}
                onChange={(event) =>
                  setCaseReference(
                    event.target.value
                  )
                }
                placeholder="Case / ticket / incident ID"
              />
            </label>

            <label>
              Evidence Title

              <input
                value={title}
                disabled={busy}
                onChange={(event) =>
                  setTitle(
                    event.target.value
                  )
                }
                placeholder="Short evidence title"
              />
            </label>

            <label>
              Retain Until

              <input
                type="date"
                value={retentionUntil}
                disabled={busy}
                onChange={(event) =>
                  setRetentionUntil(
                    event.target.value
                  )
                }
              />
            </label>

            <label className="pcapLegalHold">
              <input
                type="checkbox"
                checked={legalHold}
                disabled={busy}
                onChange={(event) =>
                  setLegalHold(
                    event.target.checked
                  )
                }
              />

              Legal Hold
            </label>

            <label className="pcapEvidenceNotes">
              Analyst Notes

              <textarea
                rows={4}
                value={notes}
                disabled={busy}
                onChange={(event) =>
                  setNotes(
                    event.target.value
                  )
                }
                placeholder="Analyst observations and case notes"
              />
            </label>
          </div>

          <button
            type="button"
            className="pcapEvidenceSave"
            disabled={busy}
            onClick={saveMetadata}
          >
            Save Evidence Metadata
          </button>

          <div className="pcapCustody">
            <h4>
              Chain of Custody
            </h4>

            {custody.length === 0 ? (
              <p>
                No custody events recorded.
              </p>
            ) : (
              <div className="pcapCustodyTimeline">
                {custody.map(
                  (event) => (
                    <div
                      className="pcapCustodyEvent"
                      key={event.id}
                    >
                      <div className="pcapCustodyDot" />

                      <div>
                        <strong>
                          {
                            event.event_type
                              ?.replaceAll(
                                "_",
                                " "
                              )
                              .toUpperCase()
                          }
                        </strong>

                        <span>
                          {
                            event.actor ||
                            "unknown"
                          }
                        </span>

                        <span>
                          {localDate(
                            event.created_at
                          )}
                        </span>

                        {event.observed_sha256 ? (
                          <code>
                            {
                              event.observed_sha256
                            }
                          </code>
                        ) : null}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </>
      )}

      <div className="pcapEvidenceBoundary">
        No automatic deletion ·
        no automatic purge ·
        archive is lifecycle metadata only ·
        PCAP remains preserved ·
        custody history is append-only
      </div>
    </section>
  );
}

export default PcapEvidencePanel;
